import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Table, Badge, Spinner } from "reactstrap";
import { listExpiringSubscriptions } from "../Subscription/_requests";
import { formatDate } from "../../helpers/FunctionHelpers";

const ExpiringSubscriptions = () => {
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [reminderDays, setReminderDays] = useState<number>(4);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchExpiring = async () => {
            setIsLoading(true);
            try {
                // api_helper's response interceptor already unwraps to the
                // response body, so `res` is the payload, not the axios response.
                const res: any = await listExpiringSubscriptions();
                setSubscriptions(res?.data?.subscriptions || []);
                setReminderDays(res?.data?.reminder_days ?? 4);
            } catch (error) {
                console.error("Error fetching expiring subscriptions:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchExpiring();
    }, []);

    // Nearest expiry is the most urgent, so colour shifts as days_left drops.
    const expiryBadgeColor = (daysLeft: number) => {
        if (daysLeft <= 1) return "danger";
        if (daysLeft <= 2) return "warning";
        return "info";
    };

    const expiryLabel = (daysLeft: number) => {
        if (daysLeft === 0) return "Expires today";
        if (daysLeft === 1) return "1 day left";
        return `${daysLeft} days left`;
    };

    return (
        <React.Fragment>
            <Col xl={12}>
                <Card>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Subscription Expiry Reminders
                        </h4>
                        <div className="flex-shrink-0">
                            <Badge color="danger" className="fs-12">
                                {subscriptions.length} expiring in {reminderDays} days
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <div className="table-responsive">
                            <Table className="align-middle table-nowrap mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Customer</th>
                                        <th>Plan</th>
                                        <th>Product Details</th>
                                        <th>Expires On</th>
                                        <th className="text-center">Days Left</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={5} className="text-center py-4">
                                                <Spinner size="sm" className="me-2" />
                                                Loading expiring subscriptions...
                                            </td>
                                        </tr>
                                    ) : subscriptions.length > 0 ? (
                                        subscriptions.map((item: any, index: number) => (
                                            <tr key={item.id || index}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="fs-14 mb-1">
                                                                {item.user?.name || "Unknown"}
                                                            </h6>
                                                            <p className="text-muted mb-0">
                                                                {item.user?.mobile_number}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{item?.subscription?.name || "N/A"}</td>
                                                <td>
                                                    {item?.details?.map((detail: any, idx: number) => (
                                                        <div key={idx} className="small">
                                                            {detail.product?.name}
                                                        </div>
                                                    ))}
                                                </td>
                                                <td>{item.end_date ? formatDate(item.end_date) : "N/A"}</td>
                                                <td className="text-center">
                                                    <Badge
                                                        color={expiryBadgeColor(Number(item.days_left))}
                                                        className="text-uppercase"
                                                    >
                                                        {expiryLabel(Number(item.days_left))}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-4">
                                                No subscriptions expiring in the next {reminderDays} days.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </React.Fragment>
    );
};

export default ExpiringSubscriptions;
